import { IColumn, IIconProps, useTheme } from '@fluentui/react';
import AppContext, { IAppContext } from 'App/AppContext';
import {
  IPActionButton,
  IPFontIcon,
  IPIconButton,
  IPLabel,
  IPLink,
  IPSeparator,
  IPShimmeredDetailsList,
  IPSpinner,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import AICriterionOutcomeDetails from 'components/AuditCriteria/AICriterionOutcomeDetails';
import EvidenceTaskLinks from 'components/AuditCriteria/EvidenceTaskLinks';
import GenericModal from 'components/Dialogs/GenericModal';
import AddNewLink from 'components/Links/AddNewLink';
import HrefLink from 'components/Links/HrefLink';
import TextInfo from 'components/Notification/Info';
import RichTextEditor from 'components/Text/RichTextEditor';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import {
  deleteIcon,
  downArrowIcon,
  editIcon,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  globalTextStylesError,
  newIcon,
  upArrowIcon,
} from 'globalStyles';
import {
  auditCriterionChangeKindArchived,
  auditCriterionChangeKindNew,
  auditCriterionChangeKindReworded,
  getAuditCheckStatusText,
  getAuditCriterionNotAuditedText,
  isAuditCheckStatusImpliedByAnswer,
  isEvidenceTaskVerified,
} from 'models/auditCriteria';
import { getOutcomeIdFromRunRef } from 'models/auditCriteriaGenericSets';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import { EntityTypes } from 'models/entity';
import KPI, { KPITypes } from 'models/kpi/kpi';
import KPIData from 'models/kpi/kpiData';
import KPIDataContext from 'models/kpi/kpiDataContext';
import ResourceLink from 'models/resourceLink';
import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import KpiAttachmentLocation from 'models/kpiAttachmentLocation';
import ResourceList from 'models/resourceList';
import TaskTypeFormElement, { TaskTypeFieldType } from 'models/tasks/taskTypeFormElement';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetFieldAttachmentLocation, apiGetKpiAttachmentLocation } from 'services/Api/kpiAttachmentLocationService';
import { apiGetLinksForId } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { IPSharedColors } from 'themes/runtime/fluentColors';
import AppError from 'utils/appError';
import { htmlToText } from 'utils/html';
import { isEmpty } from 'utils/string';
import { ISingleFormElementProps } from './SingleFormElement';

const SkypeCircleCheckIconSmall: IIconProps = {
  iconName: 'SkypeCircleCheck',
  style: {
    fontSize: 12,
    color: IPSharedColors.green20,
  },
};

const StatusErrorFullIconSmall: IIconProps = {
  iconName: 'StatusErrorFull',
  style: {
    fontSize: 12,
    color: IPSharedColors.red20,
  },
};

const getStatusIcon = (kpi: KPI): JSX.Element | null => {
  const max = kpi.getMinAttachments();
  const current = kpi.attachments?.length ?? 0;
  if (kpi.attachmentMode && kpi.attachmentMode > 0) {
    if (current >= max) return <IPFontIcon {...SkypeCircleCheckIconSmall} />;
    if (current < max) return <IPFontIcon {...StatusErrorFullIconSmall} />;
  }

  return null;
};

const kpiHasNoValue = (kpi: KPI): boolean => {
  return kpi.data.length === 0 || !kpi.data[0].hasValue();
};

//
// Wrapper
//
export const SingleFormElementWrapper = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpi', 'kpis', 'task', 'criteria']);
  const appContext = useContext(AppContext);
  const [hover, setHover] = useState<boolean>(false);
  const theme = useTheme();
  const minHeight = props.allowEdit ? 68 : 32;

  // Projected from an entity's audit criteria: the criteria set owns the QUESTION, so the form
  // editor may not touch it as a plain field. The ANSWER is not locked by this - see
  // SingleFormElement.
  const isCriterionField = !!props.field.criterionKey;
  // A one-time criterion asks the auditor to inspect evidence that already exists, a recurring one to
  // assess ongoing practice. Worth a badge: it changes what the auditor is supposed to go and look at.
  // The badge reads the bindings the caller filtered to one-time, the same ones the execution forms
  // link below the question.
  //
  // criterionOneTime is the exception: the criteria editor renders a local projection of the set, where
  // the criterion's own evidence mode is known, so a one-time criterion keeps its badge there before any
  // evidence task is bound to it. Nothing sets it on a server-projected form.
  const evidenceTasks = props.field.criterionKey
    ? props.evidenceTasksByCriterionKey?.get(props.field.criterionKey.toLowerCase()) ?? []
    : [];
  const hasOneTimeEvidenceTask = evidenceTasks.length > 0;
  const isOneTimeCriterion = isCriterionField && (hasOneTimeEvidenceTask || props.field.criterionOneTime === true);
  // Three badge states, the same as the overview column: red without a task, orange with a task
  // nobody completed yet, green once a task is completed or archived. Items built from binding rows
  // carry no task state and keep the green of "a task is in place".
  const hasVerifiedEvidenceTask = evidenceTasks.some(isEvidenceTaskVerified);
  const hasVerificationInfo = evidenceTasks.some((task) => task.taskClosed !== undefined);
  // The standards a criterion belongs to, as chips. Only the criteria editor's local projection sets
  // them: the server-projected procedure and execution forms carry none, so the auditee never sees a
  // standard on the audit task. Shown only when the tenant has several active standards to tell apart.
  const normIdsKey = (props.field.normIds ?? []).join(',');
  const [activeNormNames, setActiveNormNames] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    if (!normIdsKey) return;
    let cancelled = false;
    void appContext.globalDataCache.norms.getItems().then((norms) => {
      if (!cancelled) {
        setActiveNormNames(new Map(norms.filter((norm) => norm.active).map((norm) => [norm.normId, norm.name])));
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normIdsKey]);
  const showStandardChips = isCriterionField && !!normIdsKey && activeNormNames.size > 1;
  // "Answered by" and the latest AI outcome of a criterion: only the criteria editor passes them (PLAN-075)
  const criterionBadges = props.field.criterionKey
    ? props.criterionBadgesByKey?.get(props.field.criterionKey.toLowerCase()) ?? []
    : [];
  // A criterion-derived question cannot be edited as a FORM FIELD: the criteria set owns it, and a
  // form author who deleted it would only get it back on the next projection. It IS editable as a
  // CRITERION where the host offers that route (the procedure form editor and the criteria editor
  // pass onEditCriterion); the pencil then opens the criterion editor instead of the field editor.
  const allowFieldEdit = props.allowEdit && !isCriterionField;
  const allowCriterionEdit = props.allowEdit && isCriterionField && !!props.onEditCriterion;

  // The command column keeps its space whether or not the mouse is over the card: rendering the
  // buttons only on hover made every row grow the moment it was pointed at, and a list that moves
  // under the cursor is unusable. visibility (not display) is what reserves the box - and it also
  // keeps the hidden buttons out of the tab order.
  const commandColumnStyles = {
    root: { visibility: hover ? 'visible' : 'hidden', height: '100%' } as const,
  };

  // A standard chip names a standard and carries no state; neither does the one-time badge where the host passes no
  // evidence tasks at all (a generic set: evidence tasks are per item).
  const neutralBadgeColors = { text: theme.semanticColors.bodySubtext, background: theme.palette.neutralLighter };

  const getOneTimeBadgeColors = (): { text: string; background: string } => {
    //an item that reads a generic set: the state of its latest assessment (PLAN-075 Task 30, spec 5.4)
    const oneTimeState = isAuditCriteriaPreviewEnabled(appContext) ? props.field.criterionOneTimeState : undefined;
    if (oneTimeState === 'Verified') {
      return { text: theme.semanticColors.successText, background: theme.semanticColors.successBackground };
    }
    if (oneTimeState === 'Pending') {
      return { text: theme.semanticColors.severeWarningIcon, background: theme.semanticColors.severeWarningBackground };
    }
    if (oneTimeState === 'Missing') {
      return { text: theme.semanticColors.errorText, background: theme.semanticColors.errorBackground };
    }
    if (!props.evidenceTasksByCriterionKey) return neutralBadgeColors;
    if (!hasOneTimeEvidenceTask) {
      return { text: theme.semanticColors.errorText, background: theme.semanticColors.errorBackground };
    }
    if (hasVerificationInfo && !hasVerifiedEvidenceTask) {
      return { text: theme.semanticColors.severeWarningIcon, background: theme.semanticColors.severeWarningBackground };
    }

    return { text: theme.semanticColors.successText, background: theme.semanticColors.successBackground };
  };

  const oneTimeBadgeColors = getOneTimeBadgeColors();
  const oneTimeBadgeStyles = {
    root: {
      color: oneTimeBadgeColors.text,
      backgroundColor: oneTimeBadgeColors.background,
      border: `1px solid ${oneTimeBadgeColors.text}`,
      borderRadius: 10,
      padding: '1px 8px',
      whiteSpace: 'nowrap',
    },
  };

  // What a re-evaluation did to the criterion since the set's last review: only the criteria editor's
  // local projection carries it, so the reviewer sees which cards to go through.
  const getChangeBadgeText = (): string | undefined => {
    switch (props.field.criterionChangeKind) {
      case auditCriterionChangeKindNew:
        return t('criteria:Editor.Change.New');
      case auditCriterionChangeKindReworded:
        return t('criteria:Editor.Change.Reworded');
      case auditCriterionChangeKindArchived:
        return t('criteria:Editor.Change.Archived');
      default:
        return undefined;
    }
  };

  const changeBadgeText = isCriterionField ? getChangeBadgeText() : undefined;
  const neutralBadgeStyles = {
    root: {
      ...oneTimeBadgeStyles.root,
      color: neutralBadgeColors.text,
      backgroundColor: neutralBadgeColors.background,
      border: `1px solid ${neutralBadgeColors.text}`,
    },
  };

  const changeBadgeStyles = {
    root: {
      ...oneTimeBadgeStyles.root,
      color: theme.palette.themePrimary,
      backgroundColor: theme.palette.themeLighterAlt,
      border: `1px solid ${theme.palette.themePrimary}`,
    },
  };

  //a little air under the pills: the edit/delete buttons sit directly below them on hover
  const pillColumnStyles = { root: { paddingBottom: 4 } };

  const getStackStyles = () => {
    if (props.allowEdit && (hover || props.selected)) {
      // One ring for both states: the selected card keeps this border, so the host draws no second
      // ring around it. The tint token stays dark in dark mode, where the icon buttons need contrast.
      return {
        root: {
          margin: '1px',
          minHeight: minHeight,
          border: `1px solid ${props.selected ? theme.palette.themePrimary : 'grey'}`,
          borderRadius: '3px',
          paddingTop: '5px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          background: theme.semanticColors.accentTintBackground,
          '&:hover': {
            cursor: 'text',
            'border-color': props.selected ? theme.palette.themePrimary : theme.palette.themeSecondary,
            background: theme.semanticColors.accentTintBackground,
            transition: 'all 0.3s ease',
          },
        },
      };
    } else {
      if (props.field.kpi?.hasAutomatedEvidence) {
        return {
          root: {
            margin: '1px',
            border: '1px solid #32a852',
            borderRadius: '3px',
            minHeight: minHeight,
            paddingTop: '5px',
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingBottom: '10px',
          },
        };
      } else if (props.field.errorMessage) {
        return {
          root: {
            margin: '1px',
            border: '1px solid #BC1E1E',
            borderRadius: '3px',
            minHeight: minHeight,
            paddingTop: '5px',
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingBottom: '10px',
          },
        };
      } else {
        //no accent of its own for a criterion card: the accent is drawn once around the whole block
        //of criteria of one audited item (see TaskTypeFormRenderer), which is what those questions
        //actually have in common
        return {
          root: {
            margin: '1px',
            border: '1px solid transparent',
            minHeight: minHeight,
            paddingTop: '5px',
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingBottom: '10px',
          },
        };
      }
    }
  };

  // Retired and one-time change what the auditor is supposed to DO with the question, so they are
  // worth saying at answer time too. The plain "this comes from the criteria set" note is not: it
  // only explains to a FORM AUTHOR why the question cannot be edited as a field, and at answer time
  // it fires on every criterion card in the form. Offer it only where the form is being edited.
  const getCriterionMarkerText = (): string | undefined => {
    if (props.field.retiredOn) return t('forms:Criteria.Retired');
    if (isOneTimeCriterion) return t('forms:Criteria.OneTime');
    if (!props.allowEdit) return undefined;

    return t('forms:Criteria.FromCriterion');
  };

  const getFieldTooltip = (): JSX.Element | undefined => {
    if (props.field.errorMessage) {
      return <IPText styles={globalTextStylesError}>{props.field.errorMessage}</IPText>;
    } else if (props.field.kpi?.hasAutomatedEvidence) {
      return <IPText>{t('forms:Errors.HasAutomatedEvidence')}</IPText>;
    } else if (isCriterionField) {
      const markerText = getCriterionMarkerText();

      return markerText ? <IPText>{markerText}</IPText> : undefined;
    } else {
      return undefined;
    }
  };

  // A tooltip host with no content still opens an empty callout, so hide it on exactly the condition
  // that produced no content.
  const fieldTooltip = getFieldTooltip();

  // In the design-time builder the whole field body is click-to-edit (open the field editor). The
  // inner controls are then made non-interactive so a click always opens the editor; the
  // edit/remove/move buttons live in a separate column and keep working.
  const designTimeEditable = !!props.designTime && ((allowFieldEdit && !!props.onEdit) || allowCriterionEdit);

  const onOpenEditor = () => {
    if (allowCriterionEdit) {
      props.onEditCriterion?.(props.field);

      return;
    }

    props.onEdit?.(props.field);
  };

  //
  // Main render
  //
  return (
    <IPStack
      tabIndex={0}
      onFocus={() => {
        setHover(true);
      }}
      onMouseOver={() => {
        setHover(true);
      }}
      onMouseLeave={() => setHover(false)}
      styles={getStackStyles()}
      horizontal
      horizontalAlign="space-between"
    >
      <IPStack.Item grow>
        <div
          role={designTimeEditable ? 'button' : undefined}
          tabIndex={designTimeEditable ? 0 : undefined}
          onClick={designTimeEditable ? () => onOpenEditor() : undefined}
          onKeyDown={
            designTimeEditable
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenEditor();
                  }
                }
              : undefined
          }
          style={designTimeEditable ? { cursor: 'pointer' } : undefined}
        >
          <IPTooltipHost
            calloutProps={{ directionalHint: IPDirectionalHint.topCenter, beakWidth: 0 }}
            hidden={!fieldTooltip}
            content={fieldTooltip}
          >
            <IPStack styles={designTimeEditable ? { root: { pointerEvents: 'none' } } : undefined}>
              {props.children}
              {!props.hideAttachments && <SingleFormElementAttachmentsKPI {...props} />}
              <SingleFormElementComments {...props} />
              <SingleFormElementAiVerdict {...props} />
            </IPStack>
          </IPTooltipHost>
        </div>
        {/* Only where the host opens tasks, i.e. the audit execution forms: the criteria editor passes
            no handler, its criterion editor lists the tasks itself. Outside the click-to-edit div on
            purpose: a design-time row makes its body non-interactive, and these links must stay
            clickable wherever they are rendered. */}
        {evidenceTasks.length > 0 && props.onOpenEvidenceTask && (
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item disableShrink>
              <IPText variant="small">{t('criteria:Tasks.Label')}</IPText>
            </IPStack.Item>
            <IPStack.Item grow shrink styles={{ root: { minWidth: 0 } }}>
              <EvidenceTaskLinks tasks={evidenceTasks} hideEmpty onOpenTask={props.onOpenEvidenceTask} />
            </IPStack.Item>
          </IPStack>
        )}
      </IPStack.Item>
      {/* Use minWidth to avoid jumping */}
      <IPStack.Item styles={{ root: { minWidth: props.allowEdit ? 80 : 0 } }}>
        {/* All pills of a criterion in ONE right-aligned column, one per line: the one-time badge and
            the standard chips used to be two stacks, of which only the chips wrapped, so the same card
            read as a row here and a column there depending on how many standards fitted. One stack with
            one gap token also means the spacing between two chips is the spacing between the badge and
            the first chip.
            A one-time criterion asks the auditor to go and look at evidence that already exists, so its
            badge stays visible whatever the label does - a KPI with showLabel false renders no label at
            all to hang it on. */}
        {(isOneTimeCriterion || showStandardChips || !!changeBadgeText || criterionBadges.length > 0) && (
          <IPStack horizontalAlign="end" tokens={globalStackTokensGapExtraSmall} styles={pillColumnStyles}>
            {changeBadgeText && (
              <IPStack.Item>
                <IPText variant="small" styles={changeBadgeStyles}>
                  {changeBadgeText}
                </IPText>
              </IPStack.Item>
            )}
            {isOneTimeCriterion && (
              <IPStack.Item>
                <IPText variant="small" styles={oneTimeBadgeStyles}>
                  {t('forms:Criteria.OneTimeBadge')}
                </IPText>
              </IPStack.Item>
            )}
            {showStandardChips &&
              (props.field.normIds ?? []).map((normId) => (
                <IPStack.Item key={normId}>
                  <IPText variant="small" styles={neutralBadgeStyles}>
                    {activeNormNames.get(normId) ?? `#${normId}`}
                  </IPText>
                </IPStack.Item>
              ))}
            {criterionBadges.map((badge) => (
              <IPStack.Item key={badge}>
                <IPText variant="small" styles={changeBadgeStyles}>
                  {badge}
                </IPText>
              </IPStack.Item>
            ))}
          </IPStack>
        )}
        {/* A criterion card edits, deletes and moves the CRITERION, never the form field: the
            criteria set owns the question, its order and its existence. The buttons are only
            rendered where the host offers those routes. */}
        {allowCriterionEdit && (
          <IPStack horizontal styles={commandColumnStyles} horizontalAlign="end">
            <IPStack.Item>
              <IPStack verticalFill horizontalAlign="end" verticalAlign="start">
                <IPTooltipHost content={t('forms:Dialogs.EditForm.Edit')}>
                  <IPIconButton
                    onClick={() => props.onEditCriterion?.(props.field)}
                    styles={{ root: { height: 24 } }}
                    iconProps={editIcon}
                  />
                </IPTooltipHost>
                {props.onRemoveCriterion && (
                  <IPTooltipHost content={t('criteria:Commands.Delete')}>
                    <IPIconButton
                      onClick={() => props.onRemoveCriterion?.(props.field)}
                      styles={{ root: { height: 24 } }}
                      iconProps={deleteIcon}
                    />
                  </IPTooltipHost>
                )}
              </IPStack>
            </IPStack.Item>
            {(props.onMoveCriterionUp || props.onMoveCriterionDown) && (
              <IPStack.Item>
                <IPStack verticalFill horizontalAlign="end" verticalAlign="start">
                  <IPTooltipHost content={t('forms:Dialogs.EditForm.MoveUp')}>
                    <IPIconButton
                      onClick={() => props.onMoveCriterionUp?.(props.field)}
                      styles={{ root: { height: 24 } }}
                      iconProps={upArrowIcon}
                    />
                  </IPTooltipHost>
                  <IPTooltipHost content={t('forms:Dialogs.EditForm.MoveDown')}>
                    <IPIconButton
                      onClick={() => props.onMoveCriterionDown?.(props.field)}
                      styles={{ root: { height: 24 } }}
                      iconProps={downArrowIcon}
                    />
                  </IPTooltipHost>
                </IPStack>
              </IPStack.Item>
            )}
          </IPStack>
        )}
        {allowFieldEdit && (
          <IPStack horizontal styles={commandColumnStyles} horizontalAlign="end">
            <IPStack.Item>
              <IPStack verticalFill horizontalAlign="end" verticalAlign="start">
                <IPTooltipHost content={t('forms:Dialogs.EditForm.Edit')}>
                  <IPIconButton
                    onClick={() => {
                      if (props.onEdit) props.onEdit(props.field);
                    }}
                    styles={{ root: { height: 24 } }}
                    iconProps={editIcon}
                  />
                </IPTooltipHost>
                <IPTooltipHost content={t('forms:Dialogs.EditForm.Remove')}>
                  <IPIconButton
                    onClick={() => {
                      if (props.onRemove) props.onRemove(props.field);
                    }}
                    styles={{ root: { height: 24 } }}
                    iconProps={deleteIcon}
                  />
                </IPTooltipHost>
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack verticalFill horizontalAlign="end" verticalAlign="start">
                <IPTooltipHost content={t('forms:Dialogs.EditForm.MoveUp')}>
                  <IPIconButton
                    onClick={() => {
                      if (props.onMoveUp) props.onMoveUp(props.field);
                    }}
                    styles={{ root: { height: 24 } }}
                    iconProps={upArrowIcon}
                  />
                </IPTooltipHost>

                <IPTooltipHost content={t('forms:Dialogs.EditForm.MoveDown')}>
                  <IPIconButton
                    onClick={() => {
                      if (props.onMoveDown) props.onMoveDown(props.field);
                    }}
                    styles={{ root: { height: 24 } }}
                    iconProps={downArrowIcon}
                  />
                </IPTooltipHost>
              </IPStack>
            </IPStack.Item>
          </IPStack>
        )}
      </IPStack.Item>
    </IPStack>
  );
};

//
// The category of a resolved attachment location from the lists cache, read again once when it is missing: a
// category made after the cache was loaded
//
const getAttachmentList = async (
  appContext: IAppContext,
  listId: number | undefined,
): Promise<ResourceList | undefined> => {
  if (listId === undefined) return undefined;
  const lists = appContext.globalDataCache.lists;
  if (!lists.has(listId)) await lists.getItems(true);

  return lists.has(listId) ? lists.get(listId) : undefined;
};

//the task gives the folder name its task tokens; a task that is not saved yet has none
const getSavedTaskId = (props: ISingleFormElementProps): number | undefined =>
  props.task && props.task.taskId > 0 ? props.task.taskId : undefined;

//
// Attachments KPI
//
export const SingleFormElementAttachmentsKPI = (props: ISingleFormElementProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpis', 'forms']);
  const [showAttachmentPicker, setShowAttachmentPicker] = useState<boolean>(false);
  const [showAddAttachment, setShowAddAttachment] = useState<boolean>(false);
  const [defList, setDefList] = useState<ResourceList | undefined>(undefined);
  const [location, setLocation] = useState<KpiAttachmentLocation | undefined>(undefined);
  const [isResolving, setIsResolving] = useState<boolean>(false);

  //the quick add needs a category: the KPI's own, or the tenant's KPI attachments location
  const hasLocation =
    !!props.field.kpi?.listId ||
    appContext.globalDataCache.outputLocations.getList(DocumentOutputPurpose.KpiAttachments) !== undefined;

  //the server resolves the category and the folder, and with ensure finds or creates the folder by name; with no
  //location after all the user picks a category in the attachment picker
  const openUpload = async () => {
    if (!props.field.kpi) return;
    try {
      setIsResolving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const resolved = await apiGetKpiAttachmentLocation(
        props.field.kpi.kpiId,
        getSavedTaskId(props),
        true,
        accessToken,
      );
      const list = resolved.bound ? await getAttachmentList(appContext, resolved.listId) : undefined;
      if (!list) {
        setShowAttachmentPicker(true);

        return;
      }
      setDefList(list);
      setLocation(resolved);
      setShowAddAttachment(true);
    } catch (err) {
      //shown bottom-center: the form sits in the task modal
      const msg = AppError.getControllerExceptionMsg(err, t) ?? t('forms:Fields.AttachmentLocationFailed');
      appContext.showNotification(msg, true, false, true);
    } finally {
      setIsResolving(false);
    }
  };

  const removeAttachment = (item: ResourceLink) => {
    if (!props.field.kpi) return;
    //remove from data contexts
    const newContexts = props.field.kpi?.data[0].contexts.filter(
      (c) => !(c.entityType === EntityTypes.KPILink && c.entityId === item.linkId),
    );
    props.field.kpi.data[0].contexts = newContexts;
    props.onUpdateValue(props.field.kpi, props.field.kpi.data[0]);
    //remove from the KPI attachment cache
    const newKpi = props.field.kpi?.clone();
    newKpi.attachments = props.field.kpi?.attachments?.filter((a) => a.linkId !== item.linkId);
    props.onUpdateKPI(newKpi);
  };

  const addAttachments = (links: ResourceLink[]) => {
    if (!props.field.kpi) return;
    //check max attachments and ignore if more are selected
    if (!props.field.kpi?.attachments) props.field.kpi.attachments = [];
    const max = props.field.kpi?.getMaxAttachments();
    if (props.field.kpi?.attachments.length + links.length > max) {
      links = links.slice(0, max - props.field.kpi?.attachments.length);
    }
    //create a new context item for each link
    //we assume here that the link itself was already saved
    links.forEach((link) => {
      if (props.field.kpi) {
        const newContext = new KPIDataContext();
        newContext.kpiId = props.field.kpi.kpiId;
        newContext.entityType = EntityTypes.KPILink;
        newContext.entityId = link.linkId;
        props.field.kpi?.data[0].contexts.push(newContext);
      }
    });

    //update the attachment KPI cache on the KPI itself
    props.field.kpi.attachments.push(...links);

    //push new data
    props.onUpdateValue(props.field.kpi, props.field.kpi?.data[0]);
  };

  //
  // Main render
  //
  if (
    props.field.kpi?.attachmentMode &&
    props.field.kpi?.attachmentMode !== 0 &&
    !props.field.kpi.hasAutomatedEvidence
  ) {
    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
        <IPLink onClick={() => setShowAttachmentPicker(true)} disabled={kpiHasNoValue(props.field.kpi)}>
          {props.field.kpi?.attachmentMode > 0
            ? t('kpis:Fields.AttachmentPicker.AttachmentsRequired', { count: props.field.kpi?.attachmentMode })
            : t('kpis:Fields.AttachmentPicker.AttachmentsOptional', {
                count: Math.abs(props.field.kpi?.attachmentMode),
              })}
        </IPLink>
        {getStatusIcon(props.field.kpi)}
        {hasLocation && (
          <IPActionButton
            disabled={
              props.readonly ||
              isResolving ||
              kpiHasNoValue(props.field.kpi) ||
              (props.field.kpi?.attachments ?? []).length >= Math.abs(props.field.kpi?.attachmentMode)
            }
            styles={{ root: { height: 24 } }}
            iconProps={newIcon}
            onClick={() => openUpload()}
          />
        )}
        <AddNewLink
          addLinks={addAttachments}
          existingLinks={props.field.kpi?.attachments ?? []}
          disabled={false}
          allowCustomLists={true}
          allowDocumentLibraries={true}
          allowWebURLs={true}
          allowSitePages={true}
          entityContext={true}
          selectedList={showAddAttachment ? defList : undefined}
          uploadFolderId={showAddAttachment ? location?.folderDriveItemId : undefined}
          uploadFolderName={showAddAttachment ? location?.folderName : undefined}
          hideButton={true}
          close={() => setShowAddAttachment(false)}
          hideWholeList={true}
          showCategoryContents={true}
        />
        <SingleFormElementAttachmentPickerKPI
          {...props}
          isOpen={showAttachmentPicker}
          onClose={() => setShowAttachmentPicker(false)}
          onAddAttachments={addAttachments}
          onRemoveAttachment={removeAttachment}
        />
      </IPStack>
    );
  } else {
    return null;
  }
};

//
// Attachments Field
//
export const SingleFormElementAttachmentsField = (props: ISingleFormElementProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'forms']);
  const [showAttachmentPicker, setShowAttachmentPicker] = useState<boolean>(false);
  const [showAddAttachment, setShowAddAttachment] = useState<boolean>(false);
  const [defList, setDefList] = useState<ResourceList | undefined>(undefined);
  const [location, setLocation] = useState<KpiAttachmentLocation | undefined>(undefined);
  const [isResolving, setIsResolving] = useState<boolean>(false);

  //the quick add needs a category: the question's own, or for a document question the tenant's KPI attachments
  //location, which is a document library
  const hasLocation =
    !!props.field.listId ||
    (props.field.fieldType === TaskTypeFieldType.AttachmentDocument &&
      appContext.globalDataCache.outputLocations.getList(DocumentOutputPurpose.KpiAttachments) !== undefined);

  const openUpload = async () => {
    try {
      setIsResolving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const resolved = await apiGetFieldAttachmentLocation(
        props.field.fieldId,
        getSavedTaskId(props),
        true,
        accessToken,
      );
      const list = resolved.bound ? await getAttachmentList(appContext, resolved.listId) : undefined;
      if (!list) {
        setShowAttachmentPicker(true);

        return;
      }
      setDefList(list);
      setLocation(resolved);
      setShowAddAttachment(true);
    } catch (err) {
      //shown bottom-center: the form sits in the task modal
      const msg = AppError.getControllerExceptionMsg(err, t) ?? t('forms:Fields.AttachmentLocationFailed');
      appContext.showNotification(msg, true, false, true);
    } finally {
      setIsResolving(false);
    }
  };

  const getStatusIcon = (field: TaskTypeFormElement): JSX.Element | null => {
    const max = field.getMinCount();
    const current = field.getAttachments(props.links).length ?? 0;
    if (field.fieldMode && field.fieldMode > 0) {
      if (current >= max) return <IPFontIcon {...SkypeCircleCheckIconSmall} />;
      if (current < max) return <IPFontIcon {...StatusErrorFullIconSmall} />;
    }

    return null;
  };

  const removeAttachment = (item: ResourceLink) => {
    props.removeLink(item);
  };

  const addAttachments = async (links: ResourceLink[]) => {
    //check max attachments and ignore if more are selected
    const attachments = props.field.getAttachments(props.links);
    const max = props.field.getMaxCount();
    if (attachments.length + links.length > max) {
      links = links.slice(0, max - attachments.length);
    }

    props.addLinks(links);
  };

  //
  // Main render
  //
  if (props.field.fieldMode && props.field.fieldMode !== 0) {
    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
        <IPLink onClick={() => setShowAttachmentPicker(true)}>
          {props.field.fieldMode > 0
            ? t('kpis:Fields.AttachmentPicker.AttachmentsRequired', { count: props.field.fieldMode })
            : t('kpis:Fields.AttachmentPicker.AttachmentsOptional', {
                count: props.field.getMaxCount(),
              })}
        </IPLink>
        {getStatusIcon(props.field)}
        {hasLocation && (
          <IPActionButton
            styles={{ root: { height: 24 } }}
            disabled={
              props.readonly ||
              isResolving ||
              props.field.getAttachments(props.links).length >= props.field.getMaxCount()
            }
            iconProps={newIcon}
            onClick={() => openUpload()}
          />
        )}
        <AddNewLink
          addLinks={addAttachments}
          existingLinks={props.field.getAttachments(props.links)}
          disabled={false}
          allowCustomLists={props.field.fieldType === TaskTypeFieldType.AttachmentListItem}
          allowDocumentLibraries={props.field.fieldType === TaskTypeFieldType.AttachmentDocument}
          allowWebURLs={props.field.fieldType === TaskTypeFieldType.AttachmentURL}
          allowSitePages={props.field.fieldType === TaskTypeFieldType.AttachmentPage}
          entityContext={true}
          selectedList={showAddAttachment ? defList : undefined}
          uploadFolderId={showAddAttachment ? location?.folderDriveItemId : undefined}
          uploadFolderName={showAddAttachment ? location?.folderName : undefined}
          hideButton={true}
          close={() => setShowAddAttachment(false)}
          hideWholeList={true}
          showCategoryContents={true}
        />
        <SingleFormElementAttachmentPickerField
          {...props}
          isOpen={showAttachmentPicker}
          onClose={() => setShowAttachmentPicker(false)}
          onAddAttachments={addAttachments}
          onRemoveAttachment={removeAttachment}
        />
      </IPStack>
    );
  } else {
    return null;
  }
};

//
// Attachment Picker for KPI
//
interface ISingleFormElementAttachmentPickerKPIProps extends ISingleFormElementProps {
  onClose: () => void;
  isOpen: boolean;
  onRemoveAttachment: (item: ResourceLink) => void;
  onAddAttachments: (items: ResourceLink[]) => void;
}

export const SingleFormElementAttachmentPickerKPI = (props: ISingleFormElementAttachmentPickerKPIProps) => {
  const { t } = useTranslation(['translation', 'kpis']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (
      !isLoading &&
      props.field.kpi &&
      props.field.kpi.getAttachments().filter((c) => c.entityId > 0).length >
        (props.field.kpi?.attachments?.length ?? 0)
    ) {
      loadAttachmentDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field]);

  const loadAttachmentDetails = async () => {
    try {
      if (!props.field.kpi) return;
      const attachmentContexts = props.field.kpi.getAttachments().filter((c) => c.entityId > 0);
      if (attachmentContexts && attachmentContexts.length > 0) {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const attachments = await apiGetLinksForId(
          accessToken,
          attachmentContexts.map((c) => c.entityId),
          appContext.globalDataCache,
        );

        const newKpi = props.field.kpi.clone();
        newKpi.attachments = attachments;
        props.onSetKPI(newKpi); //this sets tis initial data on the task model and original task so that they remain equal after loading
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'name',
      name: 'name',
      minWidth: 150,
      onRender: (item?: ResourceLink, index?: number, column?: IColumn) => {
        if (!item) return;

        return (
          <IPText>
            <HrefLink href={item.linkURL} external newTab underline>
              {item.linkName}
            </HrefLink>
          </IPText>
        );
      },
    },
    {
      key: 'remove',
      name: 'remove',
      minWidth: 50,
      onRender: (item?: ResourceLink, index?: number, column?: IColumn) => {
        if (!item) return;

        return (
          <IPIconButton
            disabled={props.readonly}
            iconProps={deleteIcon}
            onClick={() => props.onRemoveAttachment(item)}
          />
        );
      },
    },
  ];

  //the KPI's own category; its folder goes to the upload as the start folder
  const defList =
    props.field.kpi?.listId && appContext.globalDataCache.lists.has(props.field.kpi.listId)
      ? appContext.globalDataCache.lists.get(props.field.kpi.listId)
      : undefined;

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t('kpis:Fields.AttachmentPicker.Title', { name: props.field.name || props.field.kpi?.name })}
      minHeight={300}
      height={300}
      maxHeight={'90vh'}
      minWidth={'40vw'}
    >
      <IPStack verticalFill>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
          <AddNewLink
            selectedList={defList}
            uploadFolderId={defList ? props.field.kpi?.folderId : undefined}
            selectedListDisableOpen={true}
            addLinks={props.onAddAttachments}
            existingLinks={props.field.kpi?.attachments ?? []}
            allowCustomLists={true}
            allowDocumentLibraries={true}
            allowWebURLs={true}
            allowSitePages={true}
            disabled={
              props.readonly ||
              isLoading ||
              ((props.field.kpi?.attachmentMode ?? 0) < 0 &&
                props.field.kpi?.getAttachments().length === Math.abs(props.field.kpi?.attachmentMode ?? 0)) ||
              props.field.kpi?.getAttachments().length === 5
            }
            entityContext={true}
            hideWholeList={true}
            showCategoryContents={true}
          />
          {isLoading && <IPSpinner />}
          <IPText>
            {props.field.kpi?.attachmentMode && props.field.kpi?.attachmentMode > 0
              ? t('kpis:Fields.AttachmentPicker.Required', { count: props.field.kpi?.attachmentMode })
              : t('kpis:Fields.AttachmentPicker.Optional', {
                  count: Math.abs(props.field.kpi?.attachmentMode ?? 0),
                })}
          </IPText>
        </IPStack>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        {props.field.kpi?.getAttachments().length === 0 && (
          <IPStack.Item>
            <IPText>{t('kpis:Fields.AttachmentPicker.AttachmentsEmpty')}</IPText>
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPShimmeredDetailsList
            items={props.field.kpi?.attachments ?? []}
            columns={columns}
            compact
            enableShimmer={isLoading}
            selectionMode={IPSelectionMode.none}
            layoutMode={IPDetailsListLayoutMode.justified}
            isHeaderVisible={false}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
          />
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};

//
// Attachment Picker for Fields
//
interface ISingleFormElementAttachmentPickerFieldProps extends ISingleFormElementProps {
  onClose: () => void;
  isOpen: boolean;
  onRemoveAttachment: (item: ResourceLink) => void;
  onAddAttachments: (items: ResourceLink[]) => void;
}

export const SingleFormElementAttachmentPickerField = (props: ISingleFormElementAttachmentPickerFieldProps) => {
  const { t } = useTranslation(['translation', 'kpis']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (
      !isLoading &&
      props.task &&
      props.task.resourceLinkIds &&
      props.task.resourceLinkIds.filter((id) => id > 0).length > (props.field.getAttachments(props.links).length ?? 0)
    ) {
      loadAttachmentDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field]);

  const loadAttachmentDetails = async () => {
    try {
      if (!props.task) return;
      if (props.task.resourceLinkIds && props.task.resourceLinkIds.length > 0) {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const attachments = await apiGetLinksForId(accessToken, props.task.resourceLinkIds, appContext.globalDataCache);
        const listType = props.field.getListTypeOfAttachmentFieldType();
        props.setLinks(attachments.filter((a) => a.list.listType === listType));
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'name',
      name: 'name',
      minWidth: 150,
      onRender: (item?: ResourceLink, index?: number, column?: IColumn) => {
        if (!item) return;

        return (
          <IPText>
            <HrefLink href={item.linkURL} external newTab underline>
              {item.linkName}
            </HrefLink>
          </IPText>
        );
      },
    },
    {
      key: 'remove',
      name: 'remove',
      minWidth: 50,
      onRender: (item?: ResourceLink, index?: number, column?: IColumn) => {
        if (!item) return;

        return (
          <IPIconButton
            disabled={props.readonly}
            iconProps={deleteIcon}
            onClick={() => props.onRemoveAttachment(item)}
          />
        );
      },
    },
  ];

  //the question's own category; its folder goes to the upload as the start folder
  const defList =
    props.field.listId && appContext.globalDataCache.lists.has(props.field.listId)
      ? appContext.globalDataCache.lists.get(props.field.listId)
      : undefined;

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={props.field.name}
      minHeight={300}
      height={300}
      maxHeight={'90vh'}
      minWidth={'40vw'}
    >
      <IPStack verticalFill>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
          <AddNewLink
            selectedList={defList}
            uploadFolderId={defList ? props.field.folderId : undefined}
            selectedListDisableOpen={true}
            addLinks={props.onAddAttachments}
            existingLinks={props.field.getAttachments(props.links)}
            allowCustomLists={props.field.fieldType === TaskTypeFieldType.AttachmentListItem}
            allowDocumentLibraries={props.field.fieldType === TaskTypeFieldType.AttachmentDocument}
            allowWebURLs={props.field.fieldType === TaskTypeFieldType.AttachmentURL}
            allowSitePages={props.field.fieldType === TaskTypeFieldType.AttachmentPage}
            disabled={
              props.readonly ||
              isLoading ||
              ((props.field.fieldMode ?? 0) < 0 &&
                props.field.getAttachments(props.links).length === Math.abs(props.field.fieldMode ?? 0)) ||
              props.field.getAttachments(props.links).length === 10
            }
            entityContext={true}
            hideWholeList={true}
            showCategoryContents={true}
          />
          <IPText>
            {props.field.fieldMode && props.field.fieldMode > 0
              ? t('kpis:Fields.AttachmentPicker.Required', { count: props.field.fieldMode })
              : t('kpis:Fields.AttachmentPicker.Optional', {
                  count: Math.abs(props.field.fieldMode ?? 0),
                })}
          </IPText>
        </IPStack>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        {props.field.getAttachments(props.links).length === 0 && (
          <IPStack.Item grow>
            <IPText>{t('kpis:Fields.AttachmentPicker.AttachmentsEmpty')}</IPText>
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPShimmeredDetailsList
            items={props.field.getAttachments(props.links)}
            columns={columns}
            compact
            enableShimmer={isLoading}
            selectionMode={IPSelectionMode.none}
            layoutMode={IPDetailsListLayoutMode.justified}
            isHeaderVisible={false}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
          />
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};

//
// Comments
//
export const SingleFormElementComments = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['translation', 'kpis', 'task']);
  const [showCommentEditor, setShowCommentEditor] = useState<boolean>(false);

  const updateComment = (text: string | undefined) => {
    if (!props.field.kpi) return;
    //the editor is hidden when readonly, but blur and change can still fire on the way there: a
    //frozen answer must not go dirty on its way out
    if (props.readonly === true) return;
    const newData = props.field.kpi?.data[0];
    newData.resultComment = text;
    props.onUpdateValue(props.field.kpi, newData);
  };

  //
  // Main render
  //
  if (props.field.kpi?.commentMode && props.field.kpi?.commentMode !== 0 && !props.field.kpi.hasAutomatedEvidence) {
    // Get current value
    const newValue = props.field.kpi?.data[0].resultComment;

    // Determine minLength and maxLength: a positive comment mode demands a comment (and how long it
    // must be), a negative one sets its own maximum. Both count visible characters; the editor below
    // only guards the stored HTML, which is longer than the text the user sees.
    const minLength = props.field.kpi.getMinComment();
    const maxLength = props.field.kpi.getMaxComment();

    // Calculate remaining characters - counted the way the form validates them, or the hint and the
    // error it is supposed to help clear disagree
    const textLen = props.field.kpi?.data[0].getCommentLength() ?? 0;
    let remaining = (minLength ?? 0) - textLen;
    if (remaining < 0) remaining = 0;

    // A comment is an answer like any other: it is what keeps a retired criterion on the form, and it
    // is the auditor's own work. So it locks with the rest of the form - on a completed audit, a
    // finished task, or a retired field there is nothing left to write here. Without this the editor
    // stayed open beside fields that were already read-only.
    const readonly = props.readonly === true;

    return (
      <IPStack>
        {!readonly && (
          <IPLink id={`Q${props.field.kpi?.kpiId}CommentCallout`} onClick={() => setShowCommentEditor(true)}>
            {props.field.kpi?.commentMode > 0 ? t('kpis:Fields.CommentsRequired') : t('kpis:Fields.CommentsOptional')}
          </IPLink>
        )}
        {(newValue || (showCommentEditor && !readonly)) && (
          <RichTextEditor
            html={newValue}
            readOnly={readonly}
            directEdit={showCommentEditor && !readonly}
            onChange={(value) => {
              updateComment(value);
            }}
            onAccept={(value, accept) => {
              setShowCommentEditor(false);
              if (accept) {
                updateComment(value || undefined);
              } else {
                updateComment(newValue);
              }
            }}
            onStartEdit={() => {
              setShowCommentEditor(true);
            }}
            placeholder={t('task:CheckList.AddComment')}
            onBlur={(lastValue) => {
              setShowCommentEditor(false);
              updateComment(lastValue);
            }}
            maxLength={KPIData.commentMaxLength}
            maxHeight={-1}
            hideRemainingChars={true}
            hideBorder={true}
            hideEditIcon={true}
            disallowFullscreen={true}
          />
        )}
        {/* A minimum of one is the plain "a comment is required" the link above already says, so it
            counts up like an optional comment instead of counting down towards one character */}
        {showCommentEditor && (minLength <= 1 || remaining === 0) && (
          <IPText variant="small" styles={textLen > maxLength ? globalTextStylesError : undefined}>
            {t('translation:General.Text.LengthOfMax', { len: textLen, max: maxLength })}
          </IPText>
        )}
        {showCommentEditor && minLength > 1 && remaining > 0 && (
          <IPText variant="small">{t('translation:General.Text.MinLength', { len: remaining, min: minLength })}</IPText>
        )}
      </IPStack>
    );
  } else {
    return null;
  }
};

//
// AI verdict
//
export const SingleFormElementAiVerdict = (props: ISingleFormElementProps) => {
  // 'criteria' is in the list for the evidence-status texts getAuditCheckStatusText resolves: a
  // namespace the caller does not load leaves the key on screen raw
  const { t } = useTranslation(['translation', 'forms', 'criteria']);
  const appContext = useContext(AppContext);
  const theme = useTheme();

  const kpi = props.field.kpi;
  const data = kpi?.data[0];

  // Writes the AI answer into the ANSWER, never the other way round, and into local state only - the
  // panel's Save is still what stores it, exactly like the whole-task take-over on the audit pane.
  const takeOverAiValue = () => {
    if (!kpi || !data) return;
    const newData = data;
    newData.resultNumber = newData.aiResultNumber;
    newData.resultText = newData.aiResultText;
    newData.resultDate = newData.aiResultDate;
    props.onUpdateValue(kpi, newData);
  };

  // What the AI's verdict reads as. The status travels beside the value because the pass/fail answer
  // does not say it all - Contradicted is stored as a No, and partly met has no value (an unclear status
  // is Inconclusive) - but it is a raw contract name ("PartiallySatisfied"), so it is only ever shown in
  // plain language. Satisfied and NotSatisfied are exactly the yes and the no the value already says, and are left out.
  //
  // The same field carries a REASON instead of a status when the run did not judge this criterion -
  // it is the auditor's own, nothing was bound to it, or it could not be inspected. Saying which is
  // the whole point: a criterion the run skipped used to render nothing, which was indistinguishable
  // on screen from never having run the audit at all.
  const getVerdictText = (): string => {
    const value = data?.getAiValue(t);
    const notAuditedText = getAuditCriterionNotAuditedText(data?.aiResultComment, t);
    if (notAuditedText) return notAuditedText;

    const statusText = getAuditCheckStatusText(data?.aiResultComment, t);
    if (!value) return statusText ?? t('forms:Criteria.Ai.NotInspected');
    if (!statusText || isAuditCheckStatusImpliedByAnswer(data?.aiResultComment)) return value;

    return `${value} (${statusText})`;
  };

  //
  // Main render
  //
  if (!kpi || !data?.hasAiVerdict()) {
    return null;
  }

  // The row above is the comment block. With nothing written it is only the "add comment" link,
  // which brings no spacing of its own, so without a gap here the AI answer reads as a second line
  // of that link. A written comment ends in the editor's own padding and needs no gap on top of it.
  const hasComment = !isEmpty(htmlToText(data.resultComment));

  // The verdict and the offer to adopt it are one link: the whole point of the line is the answer, and
  // a separate button repeated "this answer" beside text that was already the answer. There is nothing
  // to adopt on a frozen field, nor when the run returned a reason instead of a verdict, and then the
  // same sentence is plain muted text - it still has to say what the AI made of the criterion.
  const canTakeOver = data.hasAiAnswer() && !props.readonly && !props.field.retiredOn;
  // an automation's outcome put this AI answer here (PLAN-075, spec 5.7): it says so, and its hover shows the outcome's
  // summary and evidence; taking it over is the person's answer, which marks the outcome Accepted on the save
  const outcomeId = isAuditCriteriaPreviewEnabled(appContext) ? getOutcomeIdFromRunRef(data.aiRunRef) : undefined;

  const verdict = canTakeOver ? (
    <IPLink onClick={takeOverAiValue}>
      {outcomeId
        ? t('forms:Criteria.Ai.ProposedTakeOver', { answer: getVerdictText() })
        : t('forms:Criteria.Ai.TakeOver', { answer: getVerdictText() })}
    </IPLink>
  ) : (
    // muted and read-only on purpose: this is not the recorded answer until a human adopts it
    <IPText styles={{ root: { color: theme.palette.neutralSecondary } }}>
      {outcomeId
        ? t('forms:Criteria.Ai.Proposed', { answer: getVerdictText() })
        : t('forms:Criteria.Ai.Label', { answer: getVerdictText() })}
    </IPText>
  );

  return (
    <IPStack styles={{ root: { paddingTop: hasComment ? undefined : 8 } }}>
      {outcomeId ? (
        <IPTooltipHost content={<AICriterionOutcomeDetails outcomeId={outcomeId} textOnly />}>{verdict}</IPTooltipHost>
      ) : (
        verdict
      )}
    </IPStack>
  );
};

//
// Label with required indication and info on result and instruction texts for KPI
//
export const SingleFormElementInfoKPI = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['translation', 'kpis']);

  const getText = (): JSX.Element | undefined => {
    let frg1: JSX.Element | null = null;
    let frg2: JSX.Element | null = null;

    if (!hasInstructionOrExpectedResult()) return undefined;

    if (!isEmpty(props.field.kpi?.instruction)) {
      frg1 = (
        <Fragment>
          <IPText styles={globalTextStylesBold} block>
            {t('kpis:Fields.Instruction')}
          </IPText>
          <IPText>{props.field.kpi?.instruction}</IPText>
        </Fragment>
      );
    }

    if (!isEmpty(props.field.kpi?.expectedResult)) {
      frg2 = (
        <Fragment>
          <IPText styles={globalTextStylesBold} block>
            {t('kpis:Fields.ExpectedResult')}
          </IPText>
          <IPText>{props.field.kpi?.expectedResult}</IPText>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {frg1}
        {frg1 && frg2 && <IPSeparator />}
        {frg2}
      </Fragment>
    );
  };

  const showLabel = (): boolean => {
    // In a design-time preview always show the field name so the user can identify it, even when the
    // KPI is configured to hide its label at runtime (e.g. a reused KPI with showLabel = false).
    // A Success/Error field renders its question text as the field body, and the API requires that
    // question whenever the label is hidden, so the question already identifies the field at design
    // time. The override would put the name next to the question, which for a criterion card (its
    // KPI name IS the question) and for any field named after its question reads as the same text
    // twice - the stored showLabel (false) is respected instead.
    if (props.field.criterionKey || props.field.kpi?.type === KPITypes.SuccesError) {
      return props.field.kpi?.showLabel ?? true;
    }

    return !!props.designTime || (props.field.kpi?.showLabel ?? true);
  };

  const hasInstructionOrExpectedResult = (): boolean => {
    return !isEmpty(props.field.kpi?.instruction) || !isEmpty(props.field.kpi?.expectedResult);
  };

  //
  // Main render
  //
  if (props.compact || !showLabel()) {
    // designTime rows set pointerEvents:none on the wrapper (the row click opens the editor); re-enable
    // it here so the info (i) tooltip stays hoverable.
    return (
      <div style={props.designTime ? { pointerEvents: 'auto' } : undefined}>
        <TextInfo message={getText()}>
          <IPStack horizontal>
            {showLabel() && <IPLabel>{props.field.kpi?.name}</IPLabel>}
            {props.field.kpi?.required && <SingleFormElementRequiredIndication />}
          </IPStack>
        </TextInfo>
      </div>
    );
  } else {
    return (
      <IPStack styles={{ root: { marginBottom: 5 } }}>
        <IPStack horizontal>
          <IPLabel>{props.field.kpi?.name}</IPLabel>
          {props.field.kpi?.required && <SingleFormElementRequiredIndication />}
        </IPStack>
        <IPText block>{props.field.kpi?.instruction}</IPText>
        <IPText>{props.field.kpi?.expectedResult}</IPText>
      </IPStack>
    );
  }
};

//
// Label with required indication and result and instruction texts for Fields
//
export const SingleFormElementInfoField = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['translation', 'kpis']);

  const getText = (): JSX.Element | undefined => {
    let frg1: JSX.Element | null = null;
    let frg2: JSX.Element | null = null;

    if (!hasInstructionOrExpectedResult()) return undefined;

    if (!isEmpty(props.field.instruction)) {
      frg1 = (
        <Fragment>
          <IPText styles={globalTextStylesBold} block>
            {t('kpis:Fields.Instruction')}
          </IPText>
          <IPText>{props.field.instruction}</IPText>
        </Fragment>
      );
    }

    if (!isEmpty(props.field.expectedResult)) {
      frg2 = (
        <Fragment>
          <IPText styles={globalTextStylesBold} block>
            {t('kpis:Fields.ExpectedResult')}
          </IPText>
          <IPText>{props.field.expectedResult}</IPText>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {frg1}
        {frg2 && <IPSeparator />}
        {frg2}
      </Fragment>
    );
  };

  const hasInstructionOrExpectedResult = (): boolean => {
    return !isEmpty(props.field.instruction) || !isEmpty(props.field.expectedResult);
  };

  //
  // Main render
  //
  if (props.compact) {
    // designTime rows set pointerEvents:none on the wrapper (the row click opens the editor); re-enable
    // it here so the info (i) tooltip stays hoverable.
    return (
      <div style={props.designTime ? { pointerEvents: 'auto' } : undefined}>
        <TextInfo message={getText()}>
          <IPStack horizontal>
            <IPLabel>{props.field.name}</IPLabel>
            {(props.field.fieldMode ?? 0) > 0 && <SingleFormElementRequiredIndication />}
          </IPStack>
        </TextInfo>
      </div>
    );
  } else {
    return (
      <IPStack styles={{ root: { marginBottom: 5 } }}>
        <IPStack horizontal>
          <IPLabel>{props.field.name}</IPLabel>
          {(props.field.fieldMode ?? 0) > 0 && <SingleFormElementRequiredIndication />}
        </IPStack>
        <IPText block>{props.field.instruction}</IPText>
        <IPText>{props.field.expectedResult}</IPText>
      </IPStack>
    );
  }
};

//
// Required indication
//
export const SingleFormElementRequiredIndication = () => {
  return <IPText styles={globalTextStylesError}>{'*'}</IPText>;
};
